import { defineMessages, IntlShape } from 'dibs-react-intl';
import {
    Currency,
    MATERIALS,
    RESTORATION_AND_MODIFICATIONS,
    MEDIUM,
    MORE_SIZES_AND_EDITIONS,
    METALS,
    STONES,
    STONE_CUT,
    CUSTOMIZABLE,
} from '../form/SellerApplicationFormReducer';

export const yesNoMessages = defineMessages({
    yes: {
        id: 'SellerApplicationForm.yes',
        defaultMessage: 'Yes',
    },
    no: {
        id: 'SellerApplicationForm.no',
        defaultMessage: 'No',
    },
});

export const businessFocusMessages = defineMessages({
    businessFocusFurnitureOptionLabel: {
        id: 'SellerApplicationForm.businessFocusFurnitureOptionLabel',
        defaultMessage: 'Furniture',
    },
    businessFocusFashionOptionLabel: {
        id: 'SellerApplicationForm.businessFocusFashionOptionLabel',
        defaultMessage: 'Fashion',
    },
    businessFocusJewelryOptionLabel: {
        id: 'SellerApplicationForm.businessFocusJewelryOptionLabel',
        defaultMessage: 'Jewelry',
    },
    businessFocusFineArtOptionLabel: {
        id: 'SellerApplicationForm.businessFocusFineArtOptionLabel',
        defaultMessage: 'Fine Art',
    },
    businessFocusFurnitureSelection1: {
        id: 'SellerApplicationForm.businessFocusFurnitureSelection1',
        defaultMessage: 'Vintage and Antique',
    },
    businessFocusFurnitureSelection2: {
        id: 'SellerApplicationForm.businessFocusFurnitureSelection2',
        defaultMessage: '21st Century (New and Custom)',
    },
    businessFocusFashionSelection1: {
        id: 'SellerApplicationForm.businessFocusFashionSelection1',
        defaultMessage: 'Contemporary',
    },
    businessFocusFashionSelection2: {
        id: 'SellerApplicationForm.businessFocusFashionSelection2',
        defaultMessage: 'Vintage',
    },
    businessFocusJewelrySelection1: {
        id: 'SellerApplicationForm.businessFocusJewelrySelection1',
        defaultMessage: 'Estate',
    },
    businessFocusJewelrySelection2: {
        id: 'SellerApplicationForm.businessFocusJewelrySelection2',
        defaultMessage: '21st Century (Contemporary)',
    },
});

export const businessTypeMessages = defineMessages({
    'Auction House': {
        id: 'SellerApplicationForm.businessTypeAuctionHouseOptionLabel',
        defaultMessage: 'Auction House',
    },
    Consignment: {
        id: 'SellerApplicationForm.businessTypeConsignmentOptionLabel',
        defaultMessage: 'Consignment',
    },
    Gallery: {
        id: 'SellerApplicationForm.businessTypeGalleryOptionLabel',
        defaultMessage: 'Gallery',
    },
    Showroom: {
        id: 'SellerApplicationForm.businessTypeShowroomOptionLabel',
        defaultMessage: 'Showroom',
    },
    'Design Agency': {
        id: 'SellerApplicationForm.businessTypeDesignAgencyOptionLabel',
        defaultMessage: 'Design Agency',
    },
    Maker: {
        id: 'SellerApplicationForm.businessTypeMakerOptionLabel',
        defaultMessage: 'Maker',
    },
    Artist: {
        id: 'SellerApplicationForm.businessTypeArtistOptionLabel',
        defaultMessage: 'Artist',
    },
    'Primary Market Gallery': {
        id: 'SellerApplicationForm.businessTypePrimaryMarketGalleryOptionLabel',
        defaultMessage: 'Primary Market Gallery',
    },
    'Secondary Market Gallery': {
        id: 'SellerApplicationForm.businessTypeSecondaryMarketGalleryOptionLabel',
        defaultMessage: 'Secondary Market Gallery',
    },
    Retailer: {
        id: 'SellerApplicationForm.businessTypeRetailerOptionLabel',
        defaultMessage: 'Retailer',
    },
    'Secondary Market Platform': {
        id: 'SellerApplicationForm.businessTypeSecondaryMarketPlatformOptionLabel',
        defaultMessage: 'Secondary Market Platform',
    },
    Wholesaler: {
        id: 'SellerApplicationForm.businessTypeWholesalerOptionLabel',
        defaultMessage: 'Wholesaler',
    },
    Manufacturer: {
        id: 'SellerApplicationForm.businessTypeManufacturerOptionLabel',
        defaultMessage: 'Manufacturer',
    },
    Reseller: {
        id: 'SellerApplicationForm.businessTypeResellerOptionLabel',
        defaultMessage: 'Reseller',
    },
    Individual: {
        id: 'SellerApplicationForm.businessTypeIndividualOptionLabel',
        defaultMessage: 'Individual',
    },
});

export const fieldMessages = defineMessages({
    [MATERIALS]: {
        id: 'SellerApplicationForm.inventoryDetails.materials',
        defaultMessage: 'Materials',
    },
    [RESTORATION_AND_MODIFICATIONS]: {
        id: 'SellerApplicationForm.inventoryDetails.restorationModification',
        defaultMessage: 'Restoration & Modifications',
    },
    [MEDIUM]: {
        id: 'SellerApplicationForm.inventoryDetails.medium',
        defaultMessage: 'Medium',
    },
    [MORE_SIZES_AND_EDITIONS]: {
        id: 'SellerApplicationForm.inventoryDetails.moreSizesAndEditions',
        defaultMessage: 'More Sizes and Editions (e.g. 16 x 20, Edition of 50)',
    },
    [METALS]: {
        id: 'SellerApplicationForm.inventoryDetails.metals',
        defaultMessage: 'Metals',
    },
    [STONES]: {
        id: 'SellerApplicationForm.inventoryDetails.stone',
        defaultMessage: 'Stone',
    },
    [STONE_CUT]: {
        id: 'SellerApplicationForm.inventoryDetails.stoneCut',
        defaultMessage: 'Stone Cut',
    },
    [CUSTOMIZABLE]: {
        id: 'SellerApplicationForm.inventoryDetails.customizable',
        defaultMessage: 'Customizable?',
    },
});

export const preferredLanguageMessages = defineMessages({
    english: {
        id: 'SellerApplicationForm.additionalDetails.english',
        defaultMessage: 'English',
    },
    italian: {
        id: 'SellerApplicationForm.additionalDetails.italian',
        defaultMessage: 'Italian',
    },
});

export const getLabel = (
    range: { to?: number; from?: number },
    selectedCurrency: Currency,
    intl: IntlShape
): string => {
    const formattedValueFrom = range.from
        ? intl.formatNumber(range.from, {
              style: 'currency',
              currency: selectedCurrency,
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
          })
        : '';
    const formattedValueTo = range.to
        ? intl.formatNumber(range.to, {
              style: 'currency',
              currency: selectedCurrency,
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
          })
        : '';

    if (!formattedValueFrom) {
        return `Under ${formattedValueTo}`;
    } else if (!formattedValueTo) {
        return `${formattedValueFrom}+`;
    } else {
        return `${formattedValueFrom} - ${formattedValueTo}`;
    }
};

export const getValue = (range: { to?: number; from?: number }): string => {
    if (!range.from) {
        return `-${range.to}`;
    } else if (!range.to) {
        return `${range.from}-`;
    } else {
        return `${range.from} - ${range.to}`;
    }
};

export function ascendingAlphabetical<T>(getField: (arg: T) => string) {
    return (a: T | null, b: T | null): number => {
        const aVal = a ? getField(a) : null;
        const bVal = b ? getField(b) : null;
        if (!aVal) {
            return 1;
        }
        if (!bVal) {
            return -1;
        }
        if (aVal === bVal) {
            return 0;
        }
        return aVal < bVal ? -1 : 1;
    };
}

export type AttributeType = Readonly<{
    readonly child?: ReadonlyArray<AttributeType> | null;
    readonly label: string | null;
    readonly status: string | null;
    readonly value: string | null;
}> | null;

type FlattenedAttributeType = Readonly<{
    readonly label: string | null;
    readonly status: string | null;
    readonly value: string | null;
}> | null;

export const flattenActiveAttributes: (
    attributes: ReadonlyArray<AttributeType>
) => FlattenedAttributeType[] = attributes => {
    const filteredAttributes = attributes.filter(attr => attr?.status === 'ACTIVE');

    return filteredAttributes.reduce((arr, option) => {
        if (option) {
            arr.push({
                label: option.label,
                status: option.status,
                value: option.value,
            });
        }
        let flattenedChild: FlattenedAttributeType[] = [];
        if (Array.isArray(option?.child) && option?.child.length) {
            flattenedChild = flattenActiveAttributes(option.child);
        }
        return arr.concat(flattenedChild);
    }, [] as FlattenedAttributeType[]);
};
